import type { PitchFrame } from "./music.ts";

export type NoteSource = "voice" | "midi";

export type NoteEvent = {
  id: string;
  noteNumber: number;
  startTime: number;
  endTime: number | null;
  source: NoteSource;
  velocity?: number;
  channel?: number;
  deviceId?: string;
  confidence?: number;
};

export type VoiceDisplayState = {
  stableNote: number | null;
  pendingNote: number | null;
  pendingFrames: number;
  missingFrames: number;
};

export const EMPTY_VOICE_DISPLAY_STATE: VoiceDisplayState = {
  stableNote: null,
  pendingNote: null,
  pendingFrames: 0,
  missingFrames: 0,
};

export function advanceVoiceDisplayNote(
  state: VoiceDisplayState,
  midiFloat: number | null,
  requiredFrames = 2,
  releaseFrames = 3,
): VoiceDisplayState {
  if (midiFloat === null || !Number.isFinite(midiFloat)) {
    const missingFrames = state.missingFrames + 1;
    return missingFrames >= releaseFrames
      ? { ...EMPTY_VOICE_DISPLAY_STATE }
      : { ...state, pendingNote: null, pendingFrames: 0, missingFrames };
  }

  if (state.stableNote !== null && Math.abs(midiFloat - state.stableNote) < 0.66) {
    return { stableNote: state.stableNote, pendingNote: null, pendingFrames: 0, missingFrames: 0 };
  }

  const candidate = Math.round(midiFloat);
  if (candidate === state.stableNote) {
    return { stableNote: candidate, pendingNote: null, pendingFrames: 0, missingFrames: 0 };
  }

  const pendingFrames = state.pendingNote === candidate ? state.pendingFrames + 1 : 1;
  if (pendingFrames >= requiredFrames) {
    return { stableNote: candidate, pendingNote: null, pendingFrames: 0, missingFrames: 0 };
  }

  return { ...state, pendingNote: candidate, pendingFrames, missingFrames: 0 };
}

export function advanceVoicePitchFrame(
  state: VoiceDisplayState,
  frame: PitchFrame | null,
  confidenceThreshold = 0.74,
  requiredFrames = 2,
  releaseFrames = 3,
): VoiceDisplayState {
  const accepted = frame?.midiFloat !== null
    && frame?.midiFloat !== undefined
    && frame.confidence >= (frame.acceptanceConfidence ?? confidenceThreshold);
  return advanceVoiceDisplayNote(
    state,
    accepted ? frame.midiFloat : null,
    requiredFrames,
    releaseFrames,
  );
}
export type ParsedMidiMessage =
  | { kind: "on"; noteNumber: number; velocity: number; channel: number }
  | { kind: "off"; noteNumber: number; velocity: number; channel: number }
  | null;

export function parseMidiMessage(data: ArrayLike<number>): ParsedMidiMessage {
  const status = data[0] ?? 0;
  const noteNumber = data[1] ?? -1;
  const velocity = data[2] ?? 0;
  const command = status & 0xf0;
  const channel = status & 0x0f;

  if (noteNumber < 0 || noteNumber > 127) return null;
  if (command === 0x90 && velocity > 0) return { kind: "on", noteNumber, velocity, channel };
  if (command === 0x80 || (command === 0x90 && velocity === 0)) {
    return { kind: "off", noteNumber, velocity, channel };
  }
  return null;
}
